import type { Currency } from '@navio/nsdk';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { NoddiBadge, NoddiBasicCard } from 'noddi-ui-web';
import { commonTexts, formatCurrencyAmount } from 'noddi-util';

import type { CustomerClaimBrand } from './customerClaimDiscount';

type CampaignPartner = {
  name: string;
  image?: string | null;
};

type Props = {
  discountPercentage?: number;
  discountAmount?: number;
  currency?: Currency;
  translations?: {
    used: string;
    activated: string;
    amount: string;
    discount: string;
    free: string;
  };
  namePublic: string;
  descriptionPublic?: string;
  campaignPartner?: CampaignPartner;
  maxUses?: number | null;
  usageLabel?: string;
  validityHint?: string;
  validForBrands?: CustomerClaimBrand[];
  validAtLabel?: string;
  className?: string;
};

/** Backend uses this English placeholder for tier-issued coupons; UI should localize via `translations.discount`. */
const GENERIC_COUPON_PUBLIC_LABEL = 'Discount';

const discountBadgeClassName = 'shrink-0 border-card-light-background bg-card-light-background font-bold text-primary';

const DiscountValueBadge = ({
  discountAmount,
  discountPercentage,
  currency,
  discountLabel
}: {
  discountAmount?: number;
  discountPercentage?: number;
  currency?: Currency;
  discountLabel: string;
}) => {
  if (discountAmount != null) {
    return (
      <NoddiBadge className={discountBadgeClassName}>
        {formatCurrencyAmount({ money: { amount: discountAmount, currency }, decimals: 0 })}
      </NoddiBadge>
    );
  }

  if (discountPercentage != null) {
    return <NoddiBadge className={discountBadgeClassName}>{discountPercentage} %</NoddiBadge>;
  }

  return <NoddiBadge className={discountBadgeClassName}>{discountLabel}</NoddiBadge>;
};

const usageIconName = (maxUses: number | null | undefined): NoddiIconName =>
  maxUses == null ? 'Repeat' : 'CircleCheck';

export function CouponCard({
  discountAmount,
  discountPercentage,
  namePublic,
  descriptionPublic,
  campaignPartner,
  maxUses,
  usageLabel,
  validityHint,
  validForBrands,
  validAtLabel = 'Valid at',
  translations,
  className,
  currency
}: Props) {
  const texts = translations ?? {
    activated: commonTexts.activated,
    used: commonTexts.used,
    amount: commonTexts.amount,
    discount: commonTexts.discount,
    free: 'Free'
  };
  const displayTitle = namePublic === GENERIC_COUPON_PUBLIC_LABEL ? texts.discount : namePublic;
  const displayDescription =
    descriptionPublic != null &&
    descriptionPublic !== '' &&
    descriptionPublic !== GENERIC_COUPON_PUBLIC_LABEL &&
    descriptionPublic !== namePublic
      ? descriptionPublic
      : null;
  const showUsage = usageLabel != null && usageLabel !== '';
  const showValidity = validityHint != null && validityHint !== '';
  const showBrands = validForBrands != null && validForBrands.length > 0;
  const showCampaignPartner = campaignPartner != null && campaignPartner.name.trim() !== '';
  const showFooter = showUsage || showValidity || showBrands || showCampaignPartner;

  return (
    <NoddiBasicCard className={cn('flex min-w-0 flex-col gap-3', className)}>
      <div className='flex flex-1 flex-col gap-1'>
        <div className='flex min-w-0 items-start justify-between gap-3'>
          <span className='min-w-0 text-lg font-semibold break-words text-primary'>{displayTitle}</span>
          <DiscountValueBadge
            discountAmount={discountAmount}
            discountPercentage={discountPercentage}
            currency={currency}
            discountLabel={texts.discount}
          />
        </div>

        {displayDescription != null && (
          <p className='text-sm break-words text-muted-foreground'>{displayDescription}</p>
        )}
      </div>

      {showFooter && (
        <div className='mt-auto flex flex-col gap-2'>
          {(showUsage || showValidity) && (
            <div className='flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground'>
              {showUsage && (
                <div className='flex min-w-0 items-center gap-2'>
                  <NoddiIcon name={usageIconName(maxUses)} size='xSmall' className='shrink-0 text-muted-foreground' />
                  <span className='break-words'>{usageLabel}</span>
                </div>
              )}
              {showValidity && (
                <div className='flex min-w-0 items-center gap-2'>
                  <NoddiIcon name='Calendar' size='xSmall' className='shrink-0 text-muted-foreground' />
                  <span className='break-words'>{validityHint}</span>
                </div>
              )}
            </div>
          )}

          {showBrands && (
            <div className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 border-t border-border pt-3'>
              <span className='text-xs font-medium tracking-wide text-muted-foreground uppercase'>{validAtLabel}</span>
              {validForBrands.map((brand) =>
                brand.logoUrl ? (
                  <img
                    key={brand.id}
                    src={brand.logoUrl}
                    alt={brand.name}
                    className='h-5 w-auto max-w-20 shrink-0 object-contain'
                  />
                ) : (
                  <span key={brand.id} className='text-xs font-medium text-primary'>
                    {brand.name}
                  </span>
                )
              )}
            </div>
          )}

          {showCampaignPartner && (
            <div className='flex min-w-0 items-center gap-2 border-t border-border pt-3'>
              {campaignPartner.image ? (
                <img src={campaignPartner.image} alt='' className='h-5 w-auto max-w-20 shrink-0 object-contain' />
              ) : null}
              <span className='text-xs font-medium tracking-wide text-muted-foreground uppercase'>
                {campaignPartner.name}
              </span>
            </div>
          )}
        </div>
      )}
    </NoddiBasicCard>
  );
}
